/**
 * Extranet navigation sidebar (design `app-shell`), on the dark brand surface
 * ({@link EXTRANET_DARK_SURFACE_THEME}): the brand header (white
 * {@link BrandMark} + tenant name), the navigation entries and, when docked,
 * the collapse toggle — one design-system `Sidebar` root, whose `<nav>` is
 * the scroll container (sticky header / footer).
 *
 * Used twice by `ExtranetLayout`: docked on `lg+` (collapsible), and inside
 * the mobile `Drawer` (`onNavigate` closes it).
 *
 * The entries are the registered `extranet.sidebarMenu`
 * ({@link SidebarMenuConfigurable} — rename / hide / reorder / tooltip per
 * tenant), rendered inside the `Sidebar` root ({@link SidebarMenuHost}). Its
 * `data-component-id` wrapper is a plain block between the sticky header and
 * the footer (`mt-auto`), so the scrolling and the sticky parts are
 * unchanged. The rest of the sidebar is pure chrome — not registered.
 */

import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { Icon } from '@components/base/display';
import { Sidebar } from '@components/base/navigation';
import { ThemeOverride } from '@components/integrated/theme-override';

import { useExtranetAuthStore } from '../auth/store';
import { SIDEBAR_TEXT } from './chromeText';
import { EXTRANET_DARK_SURFACE_THEME } from './extranetTheme';
import { BrandMark } from './shell/BrandMark';
import { SidebarMenuConfigurable } from './shell/register';
import { SidebarMenuHost } from './shell/SidebarMenu';

export interface ExtranetSidebarProps {
  /** Docked mode: icons only. */
  collapsed?: boolean;
  /** Docked mode: renders the collapse toggle in the footer when given. */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Called after an entry is followed (the mobile drawer closes itself). */
  onNavigate?: () => void;
  /** Classes of the themed wrapper (visibility / sizing in the layout). */
  className?: string;
  /** Classes forwarded to the base `Sidebar` (e.g. full width in the drawer). */
  sidebarClassName?: string;
}

/** Display name of the tenant: its name from the user's tenants, else the slug. */
function useTenantName(tenantSlug: string | undefined): string {
  const tenants = useExtranetAuthStore((s) => s.tenants);
  const tenant = tenants.find((t) => t.slug === tenantSlug);
  return tenant?.name ?? tenantSlug ?? '';
}

export function ExtranetSidebar({
  collapsed = false,
  onCollapsedChange,
  onNavigate,
  className = '',
  sidebarClassName,
}: Readonly<ExtranetSidebarProps>) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const tenantName = useTenantName(tenantSlug);

  return (
    <ThemeOverride
      config={EXTRANET_DARK_SURFACE_THEME}
      className={['h-full', className].filter(Boolean).join(' ')}
    >
      <Sidebar
        collapsed={collapsed}
        onCollapsedChange={onCollapsedChange}
        aria-label={SIDEBAR_TEXT.ariaLabel}
        data-testid="extranet-sidebar"
        className={sidebarClassName}
      >
        <Sidebar.Header logo={<BrandMark size="sm" />} title={tenantName} />
        <SidebarMenuHost>
          <SidebarMenuConfigurable collapsed={collapsed} onNavigate={onNavigate} />
        </SidebarMenuHost>
        {onCollapsedChange && (
          <Sidebar.Footer>
            <Sidebar.Item
              icon={
                <Icon
                  icon={collapsed ? PanelLeftOpen : PanelLeftClose}
                  size="sm"
                  color="inherit"
                />
              }
              label={collapsed ? SIDEBAR_TEXT.expand : SIDEBAR_TEXT.collapse}
              aria-label={collapsed ? SIDEBAR_TEXT.expand : SIDEBAR_TEXT.collapse}
              onClick={() => {
                onCollapsedChange(!collapsed);
              }}
            />
          </Sidebar.Footer>
        )}
      </Sidebar>
    </ThemeOverride>
  );
}
